import { Badge, Descriptions, Divider, Image, Typography } from "antd";
import {
  CheckCircle,
  Clock,
  User,
  UserCheck,
  X,
  Printer,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { TServiceTicket, TBooking } from "~/types";
import { formatDate } from "~/utils";
import { useGetInventories } from "~/services/inventory";
import { PrintWaybill } from "~/components/admin/dispatch/print-labels/print-waybill";
import { TWaybillService } from "~/types/waybill-service";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useQueryClient } from "@tanstack/react-query";

dayjs.extend(utc);

const { Text, Title } = Typography;

interface ServiceCompletionSidebarProps {
  service: TServiceTicket | null;
  booking?: TBooking | null;
  onClose: () => void;
}

export const ServiceCompletionSidebar: React.FC<ServiceCompletionSidebarProps> = ({
  service,
  booking,
  onClose,
}) => {
  const [confirmedInventory, setConfirmedInventory] = useState(0);
  const queryClient = useQueryClient();

  // Fetch inventories with history to get confirmed counts
  const { data: inventoriesData, refetch: refetchInventories } = useGetInventories({
    serviceTicketId: service?.id,
    pageSize: 500,
  });

  // Calculate confirmed inventory count when data loads
  useEffect(() => {
    if (inventoriesData?.items && service) {
      const confirmed = inventoriesData.items.filter((item) => {
        // Check if item has history with checked=true for this service ticket
        if (!item.history || !Array.isArray(item.history)) {
          return false;
        }
        const historyEntry = item.history.find(
          (history) =>
            history.serviceTicketId === service.id &&
            history.inventoryId === item.id &&
            history.checked === true
        );
        return !!historyEntry;
      }).length;
      setConfirmedInventory(confirmed);
    } else if (service?.id) {
      // If no data yet, try to refetch
      refetchInventories();
    }
  }, [inventoriesData, service?.id, refetchInventories]);

  // Refetch when service ticket completion status changes
  useEffect(() => {
    if (service && (service.completedAt || service.completed_at)) {
      // Refetch inventories to get updated history
      refetchInventories();
      // Also invalidate inventory queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
    }
  }, [service?.completedAt, service?.completed_at, refetchInventories, queryClient]);

  if (!service || (!service.completedAt && !service.completed_at)) return null;

  const formatDateTime = (date: Date | string) => {
    const d = dayjs.utc(date).local();
    return {
      date: d.format('MMM D, YYYY'),
      time: d.format('h:mm A'),
    };
  };

  const timeIn = service.timeIn ? formatDateTime(service.timeIn) : null;
  const timeOut = service.timeOut ? formatDateTime(service.timeOut) : null;
  const completedAt = formatDateTime(service.completedAt || service.completed_at!);

  // Calculate duration
  const duration = timeIn && timeOut ? (() => {
    const start = new Date(service.timeIn!);
    const end = new Date(service.timeOut!);
    const diffMs = end.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHours}h ${diffMinutes}m`;
  })() : null;

  // Calculate inventory counts
  const totalInventory = service.inventories?.length || 0;

  return (
    <div className="h-full flex flex-col bg-[#fffff5] border-l-2 border-black">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b-2 border-black bg-[#fffff5]">
        <div className="flex items-center gap-2">
          <CheckCircle size={20} className="text-black" />
          <Title level={5} className="!mb-0 text-black font-bold uppercase">
            Completion Details
          </Title>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-[#fffef5] border-2 border-black transition-colors font-bold"
        >
          <X size={20} className="text-black" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-6 bg-[#fffff5]">
        {/* Service Info */}
        <div>
          <Text strong className="text-black text-xs uppercase tracking-wide font-bold">
            Service Information
          </Text>
          <Descriptions column={1} size="small" className="mt-2">
            <Descriptions.Item label="Service Ref">
              <Text strong className="text-black">{service.serial}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <span className="inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black bg-black text-white">
                Completed
              </span>
            </Descriptions.Item>
          </Descriptions>
        </div>

        <Divider className="!my-4 !border-black" />

        {/* Time Information */}
        <div>
          <div className="flex items-center mb-3">
            <Clock size={16} className="text-black mr-2" />
            <Text strong className="text-black text-xs uppercase tracking-wide font-bold">
              Time Information
            </Text>
          </div>
          <div className="mt-2 space-y-3">
            {timeIn && (
              <div className="p-3 bg-[#fffff5] border-2 border-black">
                <Text className="text-xs block mb-1 text-black font-bold uppercase">Time In</Text>
                <Text strong className="text-black">
                  {timeIn.date} at {timeIn.time}
                </Text>
              </div>
            )}

            {timeOut && (
              <div className="p-3 bg-[#fffff5] border-2 border-black">
                <Text className="text-xs block mb-1 text-black font-bold uppercase">Time Out</Text>
                <Text strong className="text-black">
                  {timeOut.date} at {timeOut.time}
                </Text>
              </div>
            )}

            <div className="p-3 bg-[#fffff5] border-2 border-black">
              <Text className="text-xs block mb-1 text-black font-bold uppercase">Completed</Text>
              <Text strong className="text-black">
                {completedAt.date} at {completedAt.time}
              </Text>
            </div>

            {duration && (
              <div className="p-3 bg-black text-white border-2 border-black">
                <Text className="text-xs block mb-1 font-bold uppercase">Duration</Text>
                <Text strong className="text-white">
                  {duration}
                </Text>
              </div>
            )}
          </div>
        </div>

        <Divider className="!my-4 !border-black" />

        {/* Confirmation Status */}
        <div>
          <div className="flex items-center mb-3">
            <UserCheck size={16} className="text-black mr-2" />
            <Text strong className="text-black text-xs uppercase tracking-wide font-bold">
              Confirmation Status
            </Text>
          </div>
          <div className="mt-2 space-y-2">
            {service.staffCheckoff && (
              <div className="p-2 bg-[#fffff5] border-2 border-black">
                <span className="inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black bg-black text-white">
                  Staff confirmed completion
                </span>
              </div>
            )}

            {service.adminOverride && (
              <div className="p-2 bg-[#fffff5] border-2 border-black">
                <span className="inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black bg-black text-white">
                  Admin authorized completion
                </span>
              </div>
            )}

            {totalInventory > 0 && (
              <div className="p-3 bg-[#fffff5] border-2 border-black">
                <Text className="text-xs block mb-1 text-black font-bold uppercase">Inventory Items</Text>
                <Text strong className="text-black">
                  {confirmedInventory} of {totalInventory} confirmed
                </Text>
              </div>
            )}

            {(service.addOnServices?.length || 0) > 0 && (
              <div className="p-3 bg-[#fffff5] border-2 border-black">
                <Text className="text-xs block mb-1 text-black font-bold uppercase">Add-On Services</Text>
                <Text strong className="text-black">
                  {service.addOnServices?.filter(s => s.status === 'completed').length || 0} of {service.addOnServices?.length || 0} completed
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* Completion Notes */}
        {service.completionNotes && (
          <>
            <Divider className="!my-4 !border-black" />
            <div>
              <Text strong className="text-black text-xs uppercase tracking-wide font-bold">
                Completion Notes
              </Text>
              <div className="mt-2 p-3 bg-[#fffff5] border-2 border-black">
                <Text className="text-black">{service.completionNotes}</Text>
              </div>
            </div>
          </>
        )}

        {/* Completed Tasks */}
        {service.addOnServices && service.addOnServices.filter(s => s.status === 'completed').length > 0 && (
          <>
            <Divider className="!my-4 !border-black" />
            <div>
              <div className="flex items-center mb-3">
                <CheckCircle size={16} className="text-black mr-2" />
                <Text strong className="text-black text-xs uppercase tracking-wide font-bold">
                  Completed Tasks
                </Text>
              </div>
              <div className="mt-2 space-y-2">
                {service.addOnServices
                  .filter(addOnService => addOnService.status === 'completed')
                  .map((addOnService, index) => (
                    <div key={addOnService.id} className="p-3 bg-black text-white border-2 border-black">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle size={16} className="text-white" />
                        <Text strong className="text-white">{index + 1}. {addOnService.name}</Text>
                        <span className="inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-white bg-white text-black ml-auto">
                          Completed
                        </span>
                      </div>
                      {addOnService.description && (
                        <Text className="text-sm mt-1 block text-white opacity-90">
                          {addOnService.description}
                        </Text>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}

        {/* Services Performed */}
        {(service.addOnServices?.length || 0) > 0 && (
          <>
            <Divider className="!my-4 !border-black" />
            <div>
              <div className="flex items-center mb-3">
                <CheckCircle size={16} className="text-black mr-2" />
                <Text strong className="text-black text-xs uppercase tracking-wide font-bold">
                  Services Performed
                </Text>
              </div>
              <div className="mt-2 space-y-2">
                {service.addOnServices?.map((addOnService, index) => (
                  <div key={addOnService.id} className="p-3 bg-[#fffff5] border-2 border-black">
                    <div className="flex items-center gap-2 mb-1">
                      <Text strong className="text-black">{index + 1}. {addOnService.name}</Text>
                      {addOnService.status === 'completed' && (
                        <span className="inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black bg-black text-white">
                          Completed
                        </span>
                      )}
                      {addOnService.status === 'removed' && (
                        <span className="inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black bg-gray-200 text-black">
                          Removed
                        </span>
                      )}
                      {addOnService.status === 'pending' && (
                        <span className="inline-block px-2 py-1 text-xs font-bold uppercase border-2 border-black bg-gray-200 text-black">
                          Pending
                        </span>
                      )}
                    </div>
                    {addOnService.description && (
                      <Text className="text-sm mt-1 block text-black">
                        {addOnService.description}
                      </Text>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <Divider className="!my-4 !border-black" />

        {/* Signatures */}
        <div>
          <div className="flex items-center mb-3">
            <User size={16} className="text-black mr-2" />
            <Text strong className="text-black text-xs uppercase tracking-wide font-bold">
              Signatures
            </Text>
          </div>
          <div className="mt-2 space-y-4">
            {/* Client Signature */}
            {service.clientSignature && (
              <div>
                <Text className="text-xs block mb-2 text-black font-bold uppercase">
                  Client Signature
                </Text>
                <div className="border-2 border-black p-3 bg-[#fffff5]">
                  <Image
                    src={service.clientSignature}
                    alt="Client Signature"
                    width="100%"
                    height={120}
                    style={{ objectFit: 'contain' }}
                    preview={{
                      mask: 'View Signature'
                    }}
                  />
                  {service.clientFirstName && (
                    <Text className="text-sm mt-2 block text-center font-medium text-black">
                      {service.clientFirstName}
                    </Text>
                  )}
                </div>
              </div>
            )}

            {/* Job Lead Signature */}
            {service.jobLeadSignature && (
              <div>
                <Text className="text-xs block mb-2 text-black font-bold uppercase">
                  Job Lead Signature
                </Text>
                <div className="border-2 border-black p-3 bg-[#fffff5]">
                  <Image
                    src={service.jobLeadSignature}
                    alt="Job Lead Signature"
                    width="100%"
                    height={120}
                    style={{ objectFit: 'contain' }}
                    preview={{
                      mask: 'View Signature'
                    }}
                  />
                  {service.jobLeadFirstName && (
                    <Text className="text-sm mt-2 block text-center font-medium text-black">
                      {service.jobLeadFirstName}
                    </Text>
                  )}
                </div>
              </div>
            )}

            {!service.clientSignature && !service.jobLeadSignature && (
              <Text className="text-sm text-black">
                No signatures recorded for this service.
              </Text>
            )}
          </div>
        </div>

        {/* Print Waybill Button */}
        {service && booking && (
          <>
            <Divider className="!my-4" />
            <div className="flex justify-center">
              <PrintWaybill
                size="middle"
                selectedServiceTicketsForPrint={[
                  {
                    ...service,
                    booking,
                    clientService: service.client || undefined,
                  } as TWaybillService,
                ]}
                className="w-full flex items-center justify-center gap-2 border-2 border-black bg-[#fffff5] text-black hover:bg-[#fffef5] font-bold uppercase tracking-wide"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

